import { useListActivePacks } from '../features/packs/queries';
import { PackTile } from '../components/PackTile';
import { RecentPulls } from '../features/social/RecentPulls';
import { MarketingOptinBanner } from '../components/MarketingOptinBanner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Public pack-browse grid (GET /).
 *
 * Renders the `listActivePacks` query as a Tailwind grid of PackTiles.
 * Loading/error/empty states are inline; route-level failures escape to
 * RouteErrorBoundary.
 *
 * Layout: 2 cols (mobile) / 3 (md) / 4 (lg) / 5 (xl). Mobile gets `gap-3`
 * for tighter density on narrow phones.
 *
 * Desktop (`lg+`): two-column page layout with sticky RecentPulls sidebar.
 * Mobile (`<lg`): horizontal RecentPulls strip above the heading.
 */
export function BrowsePage(): JSX.Element {
  const query = useListActivePacks();

  if (query.isLoading) {
    return (
      <section
        className="max-w-6xl mx-auto py-8 px-4 sm:px-6 space-y-6"
        data-testid="browse-loading"
      >
        <div className="lg:hidden">
          <RecentPulls layout="horizontal" />
        </div>

        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-6">
          <div className="space-y-6">
            <h1 className="text-h1">Browse packs</h1>
            <div
              data-testid="pack-grid"
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="aspect-[240/334] w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-8">
              <RecentPulls layout="vertical" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section
        className="max-w-6xl mx-auto py-8 px-4 sm:px-6 space-y-4"
        data-testid="browse-error"
      >
        <Card>
          <CardContent className="space-y-4" role="alert">
            <p className="text-body text-arcade-danger">
              <strong>Unable to load packs.</strong>{' '}
              {query.error instanceof Error ? query.error.message : 'Please try again.'}
            </p>
            <Button variant="outline" onClick={() => { void query.refetch(); }}>Retry</Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  const packs = query.data ?? [];

  if (packs.length === 0) {
    return (
      <section className="max-w-6xl mx-auto py-8 px-4 sm:px-6">
        <p className="text-body text-text-2" data-testid="browse-empty">
          No packs are available right now. Check back soon.
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-6xl mx-auto py-8 px-4 sm:px-6 space-y-6">
      <MarketingOptinBanner />

      <div className="lg:hidden">
        <RecentPulls layout="horizontal" />
      </div>

      <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-6">
        <div className="space-y-6">
          <h1 className="text-h1">Browse packs</h1>
          <div
            data-testid="pack-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
          >
            {packs.map((pack, idx) => (
              <PackTile key={pack.pack_id} pack={pack} priority={idx < 4} />
            ))}
          </div>
        </div>

        <div className="hidden lg:block">
          <div className="sticky top-8">
            <RecentPulls layout="vertical" />
          </div>
        </div>
      </div>
    </section>
  );
}
